import React, { useEffect, useState } from "react";
import CommonFooter from "../../../../core/common/footer/commonFooter";
import { Link, useNavigate } from "react-router-dom";
import { Spin, Switch } from "antd";
import {
  ROLL_CALL_API_BASE_URL,
  FILESERVER_PATH,
} from "../RollCallConfig/RollCallConfig";

const RollcallSettings = () => {
  const navigate = useNavigate();
  const [activeSetting, setActiveSetting] = useState("general");
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [locationsData, setLocationsData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getOrdinal = (day) => {
    if (!day) return "";

    const suffixes = ["th", "st", "nd", "rd"];
    const value = day % 100;

    return (
      day + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0])
    );
  };

  const formatDuration = (mins) => {
    if (!mins && mins !== 0) return "";

    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;

    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
      navigate("/signin");
    }
  }, [navigate]);

  useEffect(() => {
    if (!tokenData) return;
    if (!superIdData) return;

    const fetchOrgDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/Org/getOrgDetails?SuperId=${superIdData}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenData}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status && data.ResultData?.length > 0) {
          setOrgData(data.ResultData[0]);
        } else {
          setOrgData(null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgDetails();
  }, [tokenData]);

  useEffect(() => {
    if (!tokenData) return;
    if (!superIdData) return;

    const fetchLocationDetails = async () => {
      setLoading(true);

      try {
        const response = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/org/GetOrgLocation?SuperId=${superIdData}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenData}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status && result.data?.length > 0) {
          setLocationsData(result.data);
        } else {
          setLocationsData([]);
        }
      } catch (err) {
        console.error("Error fetching locations:", err);
        setLocationsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLocationDetails();
  }, [tokenData, superIdData]);

  useEffect(() => {
    if (!superIdData) return;

    const fetchSettings = async () => {
      setLoading(true);
      setSettingsData([]);

      try {
        const response = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/Org/getSettings?SuperId=${superIdData}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenData}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status && Array.isArray(data.ResultData)) {
          setSettingsData(data.ResultData[0]);
        } else {
          setSettingsData([]);
        }
      } catch (error) {
        console.error("Failed to fetch devices:", error);
        setSettingsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [superIdData]);

  return (
    <div>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div className="page-wrapper">
        <div className="content settings-content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/rollcall/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item " aria-current="page">
                      Settings
                    </h4>
                  </ol>
                </nav>
                <h6>Manage your settings on portal</h6>
              </div>
            </div>
            <ul className="table-top-head"></ul>
          </div>
          <div className="row">
            <div className="col-xl-12 d-block d-lg-none">
              <ul className="nav nav-tabs nav-tabs-bottom mb-3">
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeSetting === "general" ? "active" : ""}`}
                    onClick={() => setActiveSetting("general")}
                  >
                    <i className="ti ti-settings fs-18"></i>
                    <span className="fs-14 fw-medium ms-2">Information</span>
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeSetting === "system" ? "active" : ""}`}
                    onClick={() => setActiveSetting("system")}
                  >
                    <i className="ti ti-device-desktop-cog fs-18"></i>
                    <span className="fs-14 fw-medium ms-2">
                      System Settings
                    </span>
                  </a>
                </li>
              </ul>
            </div>
            <div className="col-xl-12">
              <div className="settings-wrapper d-flex">
                <div className="settings-sidebar" id="sidebar2">
                  <div className="sidebar-inner slimscroll">
                    <div id="sidebar-menu5" className="sidebar-menu">
                      <h4 className="fw-bold fs-18 mb-2 pb-2">Settings</h4>
                      <ul>
                        <li className="submenu-open">
                          <ul>
                            <li
                              className={`submenu ${activeSetting === "general" ? "active" : ""}`}
                              onClick={() => setActiveSetting("general")}
                            >
                              <Link>
                                <i className="ti ti-settings fs-18"></i>
                                <span className="fs-14 fw-medium ms-2">
                                  Information
                                </span>
                                <span className="menu-arrow" />
                              </Link>
                            </li>
                            <li
                              className={`submenu ${activeSetting === "system" ? "active" : ""}`}
                              onClick={() => setActiveSetting("system")}
                            >
                              <Link>
                                <i className="ti ti-device-desktop-cog fs-18"></i>
                                <span className="fs-14 fw-medium ms-2">
                                  System Settings
                                </span>
                                <span className="menu-arrow" />
                              </Link>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {activeSetting === "general" && (
                  <div className="card flex-fill mb-0">
                    <div className="card-header">
                      <h4 className="fs-18 fw-bold">Information</h4>
                    </div>
                    <div className="card-body">
                      <form>
                        <div className="card-title-head">
                          <h6 className="fs-16 fw-bold mb-3">
                            <span className="fs-16 me-2">
                              <i className="ti ti-user" />
                            </span>
                            Basic Information
                          </h6>
                        </div>
                        <div className="profile-pic-upload">
                          <div className="d-flex align-items-center gap-2">
                            <div className="profile-pic col-4">
                              <img
                                src={`${FILESERVER_PATH}/${orgData?.LogoPath}`}
                              />
                            </div>
                            <div className="col-6">
                              <div className="">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Organization name{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Organization name "
                                    className="form-control"
                                    value={orgData?.OrgName}
                                    disabled
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-12 col-lg-6 col-xl-4">
                            <div className="mb-3">
                              <label className="form-label">
                                Primary Email{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                placeholder="Email "
                                className="form-control"
                                value={orgData?.PrimaryEmailAddr || "N/A"}
                                disabled
                              />
                            </div>
                          </div>
                          <div className="col-md-12 col-lg-6 col-xl-4">
                            <div className="mb-3">
                              <label className="form-label">
                                Primary Mobile Number{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="tel"
                                placeholder="Mobile Number "
                                className="form-control"
                                value={orgData?.PrimaryMobile || "N/A"}
                                disabled
                              />
                            </div>
                          </div>
                          <div className="col-md-12 col-lg-6 col-xl-4">
                            <div className="mb-3">
                              <label className="form-label">
                                Secondary Email{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                placeholder="Email "
                                className="form-control"
                                value={orgData?.SecondaryEmailAddr || "N/A"}
                                disabled
                              />
                            </div>
                          </div>

                          <div className="col-md-12 col-lg-6 col-xl-4">
                            <div className="mb-3">
                              <label className="form-label">
                                Secondary Mobile Number{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="tel"
                                placeholder="Mobile Number "
                                className="form-control"
                                value={orgData?.SecondaryMobile || "N/A"}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                        <div className="card-title-head">
                          <h6 className="fs-16 fw-bold mb-3">
                            <span className="fs-16 me-2">
                              <i className="ti ti-map-pin" />
                            </span>
                            Address Information
                          </h6>
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Address <span className="text-danger">*</span>
                              </label>
                              <textarea
                                rows={2}
                                className="form-control"
                                value={orgData?.Address || "N/A"}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                        <div className="card-title-head">
                          <h6 className="fs-16 fw-bold mb-3">
                            <span className="fs-16 me-2">
                              <i className="ti ti-info-circle" />
                            </span>
                            Additional Information
                          </h6>
                        </div>
                        <div className="row">
                          {settingsData?.HasOTHrs && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-center gap-4 align-items-center">
                                  <b>OT hours </b>
                                  <Switch
                                    checked={settingsData?.HasOTHrs}
                                    disabled
                                    size="small"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {settingsData?.AutoLeaveCount && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-center gap-2 align-items-center">
                                  <b>Auto Leave Count </b>
                                  <Switch
                                    checked={settingsData?.AutoLeaveCount}
                                    disabled
                                    size="small"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {settingsData?.PayPeriodStartDay &&
                            settingsData?.PayPeriodEndDay && (
                              <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                                <div className="card w-100">
                                  <div className="card-body d-flex justify-content-between align-items-center">
                                    <b>Monthly Period</b>
                                    <span className="badge badge-secondary">
                                      {getOrdinal(
                                        settingsData?.PayPeriodStartDay,
                                      )}{" "}
                                      -
                                      {getOrdinal(
                                        settingsData?.PayPeriodEndDay,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          {settingsData?.WorkDurationMins && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                  <b>Work Duration</b>
                                  <span className="badge badge-secondary">
                                    {formatDuration(
                                      settingsData?.WorkDurationMins,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {settingsData?.TimeFormat && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                  <b>Time Format</b>
                                  <span className="badge badge-secondary">
                                    {settingsData?.TimeFormat} hrs
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {settingsData?.UserLimit && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                  <b>UserLimit</b>
                                  <span className="badge badge-secondary">
                                    {settingsData?.UserLimit}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {settingsData?.MobileAppLimit && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                  <b>MobileAppLimit</b>
                                  <span className="badge badge-secondary">
                                    {settingsData?.MobileAppLimit}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {settingsData?.EmpPortalLimit && (
                            <div className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
                              <div className="card w-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                  <b>EmpPortalLimit</b>
                                  <span className="badge badge-secondary">
                                    {settingsData?.EmpPortalLimit}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {activeSetting === "system" && (
                  <div className="card flex-fill mb-0">
                    <div className="card-header">
                      <h4 className="fs-18 fw-bold">System </h4>
                    </div>
                    <div className="card-body">
                      <form>
                        <div className="card-title-head">
                          <h6 className="fs-16 fw-bold mb-3">
                            <span className="fs-16 me-2">
                              <i className="ti ti-clock" />
                            </span>
                            Working Hours
                          </h6>
                        </div>
                        <div className="profile-pic-upload">
                          <div className="d-flex gap-2">
                            <input
                              type="time"
                              className="form-control"
                              placeholder="From"
                              value={
                                settingsData?.WorkStartTime
                                  ? settingsData.WorkStartTime
                                  : ""
                              }
                              readOnly
                              width={150}
                            />
                            <input
                              type="time"
                              className="form-control"
                              placeholder="To"
                              value={
                                settingsData?.WorkEndTime
                                  ? settingsData.WorkEndTime
                                  : ""
                              }
                              width={150}
                            />
                          </div>
                        </div>

                        <div className="card-title-head">
                          <h6 className="fs-16 fw-bold mb-3">
                            <span className="fs-16 me-2">
                              <i className="ti ti-credit-card" />
                            </span>
                            Subscription
                          </h6>
                        </div>

                        <div className="row">
                          {locationsData?.map((location) => (
                            <div
                              className="col-xl-6 col-lg-12 col-md-6 d-flex"
                              key={location.LocationId}
                            >
                              <div className="card w-100">
                                <div className="card-body">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                      <span className="avatar avatar-lg bg-light border me-2">
                                        <strong>
                                          {location.LocationName?.charAt(
                                            0,
                                          ).toUpperCase()}
                                        </strong>
                                      </span>
                                      <div>
                                        <h5 className="mb-0">
                                          {location.LocationName}
                                        </h5>
                                      </div>
                                    </div>

                                    <Link
                                      className="badge bg-outline-success"
                                      data-bs-toggle="collapse"
                                      data-bs-target={`#devices-${location.LocationId}`}
                                    >
                                      View devices
                                    </Link>
                                  </div>

                                  <p className="border-bottom pb-3 mb-3">
                                    Renewal Date:{" "}
                                    <strong>
                                      {new Date(
                                        location.RenewalDate,
                                      ).toLocaleDateString("en-GB")}
                                    </strong>
                                  </p>

                                  <div className="d-flex align-items-center justify-content-between mb-3">
                                    <Link className="btn btn-outline-success btn-sm">
                                      <i className="ti ti-wallet me-1" />
                                      Pay Now
                                    </Link>
                                    <span
                                      className={`badge ${
                                        location?.IsActive
                                          ? "bg-success"
                                          : "bg-danger"
                                      }`}
                                    >
                                      {location?.IsActive
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </div>

                                  <div
                                    className="collapse"
                                    id={`devices-${location.LocationId}`}
                                  >
                                    {location.Devices?.length === 0 && (
                                      <p className="text-muted text-center">
                                        No devices found
                                      </p>
                                    )}

                                    {location.Devices?.map((device) => (
                                      <div
                                        key={device.MachineId}
                                        className="border rounded p-3 mb-3"
                                      >
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <h6 className="mb-0">
                                            {device.DeviceName}
                                          </h6>
                                          <small className="text-muted d-block">
                                            Last Swipe:{" "}
                                            {device.LastSwipeReceived
                                              ? (() => {
                                                  const [date, time] =
                                                    device.LastSwipeReceived.split(
                                                      "T",
                                                    );
                                                  const [year, month, day] =
                                                    date.split("-");
                                                  return `${day}-${month}-${year} ${time.replace(".000Z", "")}`;
                                                })()
                                              : "—"}
                                          </small>
                                        </div>

                                        <small className="text-muted d-block">
                                          Serial No: {device.SerialNo}
                                        </small>

                                        <small className="text-muted d-block">
                                          Model: {device.DeviceModel}
                                        </small>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>
    </div>
  );
};

export default RollcallSettings;
